import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { BASE_URL } from './transports';
import { PAL_BASE_URL } from '@geneo2-web/shared-ui';
import {
  isMaintenancePayload,
  maintenanceBypassKey,
  redirectToMaintenanceFromBody,
} from './maintenance';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface ApiClientConfig {
  baseUrl?: string;
  headers?: Record<string, string>;
  /** Defaults to true (withCredentials) */
  withCredentials?: boolean;
}

const isPalAPILocal = process.env.NX_PAL_API_IS_LOCAL == 'true';

const defaultHeaders: Record<string, string> = {
  'Content-Type': 'application/json',
  'x-learning-mode': 'general',
};

function createAxiosInstance(config: ApiClientConfig = {}): AxiosInstance {
  const instance = axios.create({
    baseURL: isPalAPILocal
      ? 'http://localhost:4000'
      : config.baseUrl ?? BASE_URL,
    withCredentials: config.withCredentials ?? true,
    headers: {
      ...defaultHeaders,
      ...config.headers,
    },
  });

  instance.interceptors.request.use((reqConfig) => {
    const key = maintenanceBypassKey();
    if (key && reqConfig.headers) {
      reqConfig.headers['X-Maintenance-Bypass'] = key;
    }
    return reqConfig;
  });

  if (isPalAPILocal) {
    instance.interceptors.request.use((reqConfig) => {
      const userId = localStorage.getItem('userId');
      if (userId && reqConfig.headers) {
        reqConfig.headers['x-student-id'] = userId;
      }
      return reqConfig;
    });
  }

  instance.interceptors.response.use(
    (response) => {
      if (response.status === 503 || isMaintenancePayload(response.data)) {
        redirectToMaintenanceFromBody(
          response.status === 503 && !isMaintenancePayload(response.data)
            ? { status: 503 }
            : response.data
        );
      }
      return response;
    },
    async (error) => {
      if (error.response) {
        const res: AxiosResponse = error.response;
        if (res.status === 503 || isMaintenancePayload(res.data)) {
          redirectToMaintenanceFromBody(res.data);
        }
        const data = res.data;
        const message =
          (data && (data.message || data.error)) ||
          error.message ||
          `Request failed: ${res.status}`;
        const err = new Error(message) as Error & {
          status?: number;
          response?: AxiosResponse;
        };
        err.status = res.status;
        err.response = res;
        return Promise.reject(err);
      }
      return Promise.reject(error);
    }
  );

  return instance;
}

/**
 * Generic backend API client using Axios. All requests use BASE_URL (NX_GENEO_BACKEND_URL),
 * withCredentials: true, and default headers (including x-learning-mode).
 */
export function createApiClient(config: ApiClientConfig = {}) {
  const axiosInstance = createAxiosInstance(config);

  async function request<T = void>(
    method: HttpMethod,
    path: string,
    options: AxiosRequestConfig = {}
  ): Promise<T> {
    const response = await axiosInstance.request<T>({
      url: path,
      method,
      ...options,
    });
    return response.data;
  }

  return {
    get baseUrl() {
      return PAL_BASE_URL && PAL_BASE_URL.startsWith('http')
        ? ''
        : (axiosInstance.defaults.baseURL as string);
    },

    request<T = void>(path: string, options?: AxiosRequestConfig): Promise<T> {
      return request<T>('GET', path, options);
    },

    get<T = void>(
      path: string,
      dataOrOptions?: Record<string, unknown> | AxiosRequestConfig,
      options?: AxiosRequestConfig
    ): Promise<T> {
      const optionKeys: (keyof AxiosRequestConfig)[] = [
        'headers',
        'timeout',
        'baseURL',
        'withCredentials',
        'responseType',
        'params',
      ];
      const isOptions = (o: unknown): o is AxiosRequestConfig =>
        o != null &&
        typeof o === 'object' &&
        optionKeys.some((k) => k in (o as object));
      const hasDataArg = options !== undefined;
      let opts: AxiosRequestConfig;
      if (hasDataArg && dataOrOptions != null) {
        opts = {
          ...options,
          params: {
            ...(dataOrOptions as Record<string, unknown>),
            ...(options?.params ?? {}),
          },
        };
      } else if (dataOrOptions != null && !isOptions(dataOrOptions)) {
        opts = { params: dataOrOptions as Record<string, unknown> };
      } else {
        opts = (dataOrOptions as AxiosRequestConfig) ?? {};
      }
      return request<T>('GET', path, opts);
    },

    post<T = void>(
      path: string,
      data?: unknown,
      options?: AxiosRequestConfig
    ): Promise<T> {
      return request<T>('POST', path, { ...options, data });
    },

    put<T = void>(
      path: string,
      data?: unknown,
      options?: AxiosRequestConfig
    ): Promise<T> {
      return request<T>('PUT', path, { ...options, data });
    },

    patch<T = void>(
      path: string,
      data?: unknown,
      options?: AxiosRequestConfig
    ): Promise<T> {
      return request<T>('PATCH', path, { ...options, data });
    },

    delete<T = void>(path: string, options?: AxiosRequestConfig): Promise<T> {
      return request<T>('DELETE', path, options);
    },
  };
}

/** Shared backend API client instance (BASE_URL + withCredentials + default headers). */
export const backendApiClient = createApiClient();
